// ══════════════════════════════════════════════════════════════
// RACHA DE DÍAS CONSECUTIVOS (agregado 04/11/2026)
//
// Lógica pura, sin Firestore, para poder probarla sola.
// Los días se cuentan en horario de Argentina: si un alumno entra
// a las 23:50 y otra vez a las 00:10, son dos días distintos.
//
// Campos en Usuarios/{uid}:
//   RachaActual     → días seguidos que entró (incluye hoy).
//   RachaMaxima     → la mejor racha que tuvo.
//   UltimaConexion  → "YYYY-MM-DD" del último día que entró.
//   UltimaConexionEl→ serverTimestamp, solo para auditar.
// ══════════════════════════════════════════════════════════════

export const ZONA_HORARIA_RACHA = "America/Argentina/Buenos_Aires";

const MS_POR_DIA = 24 * 60 * 60 * 1000;

export interface EstadoRacha {
  RachaActual: number;
  RachaMaxima: number;
  UltimaConexion: string; // "" si nunca se registró
}

/** Día actual (o de `fecha`) en Argentina, como "YYYY-MM-DD". */
export const getDiaClave = (fecha: Date = new Date()): string => {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: ZONA_HORARIA_RACHA,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(fecha);
  const get = (tipo: string) => partes.find((p) => p.type === tipo)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

export const esDiaClaveValido = (valor: unknown): valor is string =>
  typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor);

const claveAUtc = (clave: string) => {
  const [y, m, d] = clave.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};

/** Días calendario entre dos claves (positivo si `hasta` es posterior). */
export const diasEntre = (desde: string, hasta: string): number =>
  Math.round((claveAUtc(hasta) - claveAUtc(desde)) / MS_POR_DIA);

const entero = (valor: unknown) =>
  typeof valor === "number" && Number.isFinite(valor)
    ? Math.max(0, Math.floor(valor))
    : 0;

/**
 * Calcula la racha nueva al entrar `hoy`.
 * - Mismo día que la última conexión → no cambia nada.
 * - Día siguiente → suma 1.
 * - Saltó uno o más días (o nunca entró) → vuelve a 1.
 * `cambio` indica si hay que escribir en Firestore.
 */
export const calcularNuevaRacha = (
  previo: Partial<EstadoRacha>,
  hoy: string,
): { estado: EstadoRacha; cambio: boolean } => {
  const actual = entero(previo.RachaActual);
  const maxima = Math.max(actual, entero(previo.RachaMaxima));
  const ultima = esDiaClaveValido(previo.UltimaConexion) ? previo.UltimaConexion : "";

  if (ultima) {
    const diferencia = diasEntre(ultima, hoy);
    // 0 = ya entró hoy. Negativo = reloj del dispositivo atrasado: no tocamos nada.
    if (diferencia <= 0) {
      return {
        estado: { RachaActual: actual, RachaMaxima: maxima, UltimaConexion: ultima },
        cambio: false,
      };
    }
    if (diferencia === 1) {
      const nueva = actual + 1;
      return {
        estado: {
          RachaActual: nueva,
          RachaMaxima: Math.max(maxima, nueva),
          UltimaConexion: hoy,
        },
        cambio: true,
      };
    }
  }

  return {
    estado: { RachaActual: 1, RachaMaxima: Math.max(maxima, 1), UltimaConexion: hoy },
    cambio: true,
  };
};

/**
 * Racha "real" para MOSTRAR. Si el alumno no entró ni hoy ni ayer, la
 * racha guardada ya está cortada aunque en Firestore siga el número
 * viejo (se corrige recién cuando vuelve a entrar). Útil en Admin.
 */
export const getRachaVigente = (
  usuario: { RachaActual?: number; UltimaConexion?: string },
  hoy: string = getDiaClave(),
): number => {
  if (!esDiaClaveValido(usuario.UltimaConexion)) return 0;
  return diasEntre(usuario.UltimaConexion, hoy) <= 1 ? entero(usuario.RachaActual) : 0;
};
